import { useState, useEffect, useCallback } from 'react';
import {
  leaveRulesApi,
  SEARCH_FIELD_OPTIONS,
  STATUS_OPTIONS,
  TOOLS_OPTIONS,
} from '../api/leaveRulesApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'name', label: 'Rule Name' },
  { key: 'quotaCount', label: 'Configured Quotas' },
  { key: 'status', label: 'Status' },
];

export function useLeaveRulesList() {
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active'); // Default to Active

  // Background Export integration
  const { triggerExport, isExporting } = useExportJob({
    module: 'leave_rules',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await leaveRulesApi.getRules({
        searchField,
        searchText,
        status: statusFilter,
        page,
        pageSize,
      });
      setRecords(res.items || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to load Leave Rules.');
    } finally {
      setIsLoading(false);
    }
  }, [searchField, searchText, statusFilter, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setSearchField('Name');
    setSearchText('');
    setStatusFilter('Active');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleArchive = async (item) => {
    const isCurrentlyActive = item.status === 'Active';
    const actionText = isCurrentlyActive ? 'Archive' : 'Activate';

    const confirmed = await appConfirm({
      title: `${actionText} Leave Rule`,
      message: `Are you sure you want to ${actionText.toLowerCase()} leave rule "${item.name}"?`,
      confirmText: actionText,
      confirmVariant: isCurrentlyActive ? 'warning' : 'primary',
    });

    if (!confirmed) return;

    try {
      const res = await leaveRulesApi.archiveRule(item.id);
      appToast.success(res?.message || `Leave rule ${actionText.toLowerCase()}d successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || `Failed to ${actionText.toLowerCase()} leave rule.`);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Leave Rule',
      message: `Are you sure you want to delete leave rule "${name || id}"? All associated leave quotas will also be deleted.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });

    if (!confirmed) return;

    try {
      await leaveRulesApi.deleteRule(id);
      appToast.success(`Leave rule "${name || id}" deleted successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || 'Failed to delete leave rule.');
    }
  };

  const handleToolSelect = (toolKey) => {
    const activeFilters = {
      search: searchText || undefined,
      status: statusFilter,
    };

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Leave Rules Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Status': statusFilter || 'All',
          'Search': searchText || 'All',
        },
        fetchReportData: async () => {
          const res = await leaveRulesApi.getLeaveRulesReport(activeFilters);
          return { items: res?.items || [] };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (toolKey === 'export_excel') {
      triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      triggerExport(activeFilters, 'PDF');
    }
  };

  return {
    state: {
      records,
      isLoading,
      isExporting,
      searchField,
      searchText,
      statusFilter,
      page,
      pageSize,
      totalCount,
      totalPages,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      tools: TOOLS_OPTIONS,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      handleStatusTabChange,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleArchive,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
